import tokenManager from './TokenManager';
import { trigger } from './events';
import { isAuthDebugEnabled } from './authDebug';

class ErrorRecoveryManager {
  constructor() {
    this.recoveryStrategies = new Map();
    this.queuedErrors = new Map();
    this.debugMode = isAuthDebugEnabled();

    this.setupRecoveryStrategies();

    if (this.debugMode) {
      console.log('🔧 ErrorRecoveryManager initialized');
    }
  }

  debug(message, data = null) {
    if (this.debugMode) {
      console.log(`🔧 ErrorRecoveryManager: ${message}`, data);
    }
  }

  setupRecoveryStrategies() {
    // Audio playback errors
    this.recoveryStrategies.set('AUDIO_PLAYBACK_FAILED', async (error) => {
      this.debug('Handling audio playback error', error);

      try {
        // Try to resume playback with fresh token
        await tokenManager.refreshToken();
        return { recovered: true, queued: false };
      } catch (refreshError) {
        this.debug('Token refresh failed for audio error', refreshError);
        return { recovered: false, queued: false };
      }
    });

    // Network errors during background
    this.recoveryStrategies.set('NETWORK_ERROR_BACKGROUND', async (error) => {
      this.debug('Handling background network error', error);

      // Queue for retry when app comes to foreground
      this.queueForRetry(error);
      return { recovered: false, queued: true };
    });

    // Authentication errors
    this.recoveryStrategies.set('AUTH_ERROR', async (error) => {
      this.debug('Handling auth error', error);

      const recovered = await this.handleAuthError(error);
      return { recovered, queued: false };
    });

    // Token expiry errors
    this.recoveryStrategies.set('TOKEN_EXPIRED', async (error) => {
      this.debug('Handling token expiry error', error);

      try {
        await tokenManager.refreshToken();
        return { recovered: true, queued: false };
      } catch (refreshError) {
        this.debug('Token refresh failed', refreshError);
        return { recovered: false, queued: false };
      }
    });

    // Rate limiting errors
    this.recoveryStrategies.set('RATE_LIMITED', async (error) => {
      this.debug('Handling rate limit error', error);

      // Wait and retry after delay
      const retryAfter = error.retryAfter || 60; // Default 60 seconds
      setTimeout(() => {
        this.retryQueuedError(error);
      }, retryAfter * 1000);

      return { recovered: false, queued: true };
    });
  }

  async handleError(error, context) {
    this.debug('Handling error', { error: error.message, context });

    const errorType = this.categorizeError(error);
    const strategy = this.recoveryStrategies.get(errorType);

    if (strategy) {
      try {
        const result = await strategy(error);
        this.debug('Error recovery result', result);
        return result;
      } catch (strategyError) {
        this.debug('Error recovery strategy failed', strategyError);
        return this.defaultErrorHandling(error, context);
      }
    }

    // Default error handling
    return this.defaultErrorHandling(error, context);
  }

  categorizeError(error) {
    // Categorize errors based on their properties
    if (
      error.code === 'auth/user-token-expired' ||
      error.code === 'auth/user-not-found'
    ) {
      return 'AUTH_ERROR';
    }

    if (error.status === 401) {
      return 'TOKEN_EXPIRED';
    }

    if (error.status === 429) {
      return 'RATE_LIMITED';
    }

    if (error.name === 'NetworkError' || error.message.includes('network')) {
      return document.hidden ? 'NETWORK_ERROR_BACKGROUND' : 'NETWORK_ERROR';
    }

    if (error.message.includes('audio') || error.message.includes('playback')) {
      return 'AUDIO_PLAYBACK_FAILED';
    }

    return 'UNKNOWN_ERROR';
  }

  async handleAuthError(error) {
    this.debug('Handling authentication error', error);

    try {
      await tokenManager.refreshToken();
      return true;
    } catch (refreshError) {
      this.debug('Auth error recovery failed', refreshError);

      // Force re-authentication
      this.triggerReauth();
      return false;
    }
  }

  triggerReauth() {
    this.debug('Triggering re-authentication');
    trigger('authError', {
      error: 'Authentication failed',
      code: 'auth/requires-reauth',
      requiresReauth: true,
    });
  }

  queueForRetry(error) {
    const errorId = `error_${Date.now()}_${Math.random()}`;
    this.queuedErrors.set(errorId, {
      error,
      timestamp: Date.now(),
      retryCount: 0,
    });

    this.debug('Error queued for retry', {
      errorId,
      queueSize: this.queuedErrors.size,
    });
  }

  async retryQueuedError(error) {
    this.debug('Retrying queued error', error);

    // Find the error in the queue
    for (const [errorId, queuedError] of this.queuedErrors.entries()) {
      if (queuedError.error === error) {
        queuedError.retryCount++;

        if (queuedError.retryCount > 3) {
          // Max retries reached, remove from queue
          this.queuedErrors.delete(errorId);
          this.debug('Max retries reached, removing from queue', { errorId });
          return;
        }

        // Retry the operation
        try {
          const result = await this.handleError(error, 'retry');
          if (result.recovered) {
            this.queuedErrors.delete(errorId);
            this.debug('Error recovered on retry', { errorId });
          }
        } catch (retryError) {
          this.debug('Retry failed', { errorId, retryError });
        }
        break;
      }
    }
  }

  processQueuedErrors() {
    this.debug('Processing queued errors', {
      queueSize: this.queuedErrors.size,
    });

    if (this.queuedErrors.size === 0) return;

    // Process all queued errors
    for (const queuedError of this.queuedErrors.values()) {
      this.retryQueuedError(queuedError.error);
    }
  }

  defaultErrorHandling(error, context) {
    this.debug('Default error handling', { error: error.message, context });

    // Log error for debugging
    console.error('Unhandled error:', error);

    // For background errors, queue them
    if (document.hidden) {
      this.queueForRetry(error);
      return { recovered: false, queued: true };
    }

    return { recovered: false, queued: false };
  }

  // Get error statistics
  getErrorStats() {
    const stats = {
      totalErrors: 0,
      recoveredErrors: 0,
      queuedErrors: this.queuedErrors.size,
      errorTypes: {},
    };

    // This would be populated with actual error tracking
    return stats;
  }

  // Cleanup method
  cleanup() {
    this.debug('Cleaning up ErrorRecoveryManager');
    this.queuedErrors.clear();
  }
}

// Create singleton instance
const errorRecoveryManager = new ErrorRecoveryManager();

export default errorRecoveryManager;
